import { collection, deleteDoc, doc, getDoc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { firebaseAuth, db, functions } from "../firebase";
import type { CustomTemplate, UserProfile } from "../types/userTypes";

/**
 * User API Service
 *
 * Handles Firestore reads/updates for the authenticated user's profile
 * and Firebase Storage uploads for profile pictures.
 */
export const userApi = {
  /**
   * Get current user's profile
   */
  getProfile: async (): Promise<UserProfile | null> => {
    const user = firebaseAuth.currentUser;
    if (!user?.uid) {
      throw new Error("No authenticated user found");
    }

    const userDoc = await getDoc(doc(db, "users", user.uid));
    if (!userDoc.exists()) return null;

    return userDoc.data() as UserProfile;
  },

  /**
   * Update current user's profile and return fresh data
   */
  updateProfile: async (
    updatedData: Partial<UserProfile>,
  ): Promise<UserProfile> => {
    const user = firebaseAuth.currentUser;
    if (!user?.uid) {
      throw new Error("No authenticated user found");
    }

    const userDocRef = doc(db, "users", user.uid);
    await updateDoc(userDocRef, {
      ...updatedData,
      updatedAt: new Date().toISOString(),
    });

    const updatedDoc = await getDoc(userDocRef);
    if (!updatedDoc.exists()) {
      throw new Error("User profile not found after update");
    }

    return updatedDoc.data() as UserProfile;
  },

  /**
   * Change the current user's specialty via Cloud Function.
   *
   * Not a direct updateDoc: specialty drives prompt routing, admin
   * analytics and the consultation config, so the server re-validates that
   * the chosen specialty exists and belongs to the account's (immutable)
   * country before writing — the same invariant createUserProfile enforces
   * at signup. firestore.rules blocks client writes to this field directly.
   */
  updateSpecialty: async (specialty: string): Promise<UserProfile> => {
    const user = firebaseAuth.currentUser;
    if (!user?.uid) {
      throw new Error("No authenticated user found");
    }

    const updateSpecialtyFn = httpsCallable<{ specialty: string }, { success: true; specialty: string }>(
      functions,
      "updateUserSpecialty",
    );
    await updateSpecialtyFn({ specialty });

    const updatedDoc = await getDoc(doc(db, "users", user.uid));
    if (!updatedDoc.exists()) {
      throw new Error("User profile not found after update");
    }

    return updatedDoc.data() as UserProfile;
  },

  /**
   * Upload a profile picture via Cloud Function (bypasses CORS).
   * Converts the file to base64 client-side and sends to the server,
   * which writes to Firebase Storage using the Admin SDK.
   *
   * @param file - A validated and pre-compressed image File (JPG or PNG).
   * @returns The public download URL stored in Firestore.
   */
  uploadProfilePicture: async (file: File): Promise<string> => {
    const user = firebaseAuth.currentUser;
    if (!user?.uid) {
      throw new Error("No authenticated user found");
    }

    const imageBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => resolve((reader.result as string).split(",")[1]);
      reader.onerror = () => reject(new Error("Failed to read image file"));
    });

    const uploadFn = httpsCallable<
      { imageBase64: string; mimeType: string },
      { downloadUrl: string }
    >(functions, "uploadProfilePicture");

    const result = await uploadFn({ imageBase64, mimeType: file.type });
    return result.data.downloadUrl;
  },

  /**
   * Remove the user's profile picture by clearing the Firestore field.
   * (The Storage object is intentionally kept for safety; storage cleanup
   * can be handled server-side via a scheduled Cloud Function.)
   */
  removeProfilePicture: async (): Promise<void> => {
    const user = firebaseAuth.currentUser;
    if (!user?.uid) {
      throw new Error("No authenticated user found");
    }

    const userDocRef = doc(db, "users", user.uid);
    await updateDoc(userDocRef, {
      profilePictureUrl: "",
      updatedAt: new Date().toISOString(),
    });
  },

  /**
   * Get user's custom templates from users/{uid}/customTemplates subcollection
   */
  getUserCustomTemplates: async (): Promise<CustomTemplate[]> => {
    const user = firebaseAuth.currentUser;
    if (!user?.uid) throw new Error("No authenticated user found");
    const snap = await getDocs(collection(db, "users", user.uid, "customTemplates"));
    // Hide templates soft-deleted via Clear All Data (deletedReason: "clear_all_data")
    // or any other soft-delete. `deletedAt` is undefined on healthy docs.
    return snap.docs
      .filter(d => !(d.data() as { deletedAt?: unknown }).deletedAt)
      .map(d => d.data() as CustomTemplate);
  },

  /**
   * Save (create or update) a single custom template.
   */
  saveUserCustomTemplate: async (template: CustomTemplate): Promise<void> => {
    const user = firebaseAuth.currentUser;
    if (!user?.uid) throw new Error("No authenticated user found");
    const raw = { ...template, updatedAt: new Date().toISOString() };
    // Firestore rejects undefined values — strip them before writing
    const entry = Object.fromEntries(
      Object.entries(raw).filter(([, v]) => v !== undefined),
    ) as unknown as CustomTemplate;
    await setDoc(doc(db, "users", user.uid, "customTemplates", template.id), entry);
  },

  /**
   * Delete a single custom template by id.
   */
  deleteUserCustomTemplate: async (templateId: string): Promise<void> => {
    const user = firebaseAuth.currentUser;
    if (!user?.uid) throw new Error("No authenticated user found");
    await deleteDoc(doc(db, "users", user.uid, "customTemplates", templateId));
  },

  /**
   * Imports a Google-hosted profile photo URL into our Storage bucket
   * via Cloud Function and returns the resulting Storage URL.
   */
  importGoogleProfilePicture: async (photoUrl: string): Promise<string> => {
    const user = firebaseAuth.currentUser;
    if (!user?.uid) {
      throw new Error("No authenticated user found");
    }

    const importFn = httpsCallable<
      { photoUrl: string },
      { downloadUrl: string }
    >(functions, "importGoogleProfilePicture");

    const result = await importFn({ photoUrl });
    return result.data.downloadUrl;
  },
};
